import { useState, useCallback, useEffect } from "react";

/**
 * Hook for managing user sessions across tabs
 * @returns {Object} Session state and methods
 */
export const useSession = () => {
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionKey, setSessionKey] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const generateSessionKey = useCallback(() => {
    return `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }, []);

  const checkSessionConflict = useCallback(() => {
    const stored = localStorage.getItem("gg_session");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.key !== sessionKey) {
          return {
            conflict: true,
            existingSession: parsed,
            message: "Another tab is already active with this session",
          };
        }
      } catch (error) {
        console.warn("Failed to parse stored session:", error);
      }
    }
    return { conflict: false };
  }, [sessionKey]);

  const saveCurrentSession = useCallback(
    (session) => {
      if (!isInitialized) return; // Prevent saving during initial load

      const sessionData = {
        ...session,
        key: sessionKey,
        timestamp: Date.now(),
      };

      setCurrentSession(session);
      localStorage.setItem("gg_session", JSON.stringify(sessionData));
    },
    [sessionKey, isInitialized]
  );

  const clearCurrentSession = useCallback(() => {
    setCurrentSession(null);
    localStorage.removeItem("gg_session");
  }, []);

  const loadPersistedSession = useCallback(() => {
    try {
      const stored = localStorage.getItem("gg_session");
      if (stored) {
        const parsed = JSON.parse(stored);
        setCurrentSession(parsed);
        return parsed;
      }
    } catch (error) {
      console.warn("Failed to load persisted session:", error);
    }
    return null;
  }, []);

  // Initialize session key and load persisted data
  useEffect(() => {
    const key = generateSessionKey();
    setSessionKey(key);

    const persistedSession = loadPersistedSession();
    if (persistedSession && persistedSession.key !== key) {
      // Handle session conflict - for now, just log
      console.warn("Session conflict detected:", persistedSession);
    }

    setIsInitialized(true);
  }, [generateSessionKey, loadPersistedSession]);

  return {
    currentSession,
    sessionKey,
    isInitialized,
    checkSessionConflict,
    saveCurrentSession,
    clearCurrentSession,
    loadPersistedSession,
  };
};
